<template>
  <div :class="$style.container">
    <HgButton
      :class="$style.button"
      theme="secondary-bordered"
      icon="Email"
      iconPosition="top"
      @click="openContactModal"
    >
      {{ $t('localLanding.floatingButton.Calling') }}
    </HgButton>
    <HgButton
      :class="$style.button"
      theme="secondary-bordered"
      icon="Calculator"
      iconPosition="top"
      @click="redirectToValuation"
    >
      {{ $t('localLanding.floatingButton.Valuation') }}
    </HgButton>
  </div>

  <InformedContactModal
    ref="contactModalRef"
    :from="CallMeBackModalFrom.RecallIcon"
    :modalType="ContactModalType.CALL_ME_BACK"
  />
</template>

<script setup lang="ts">
import { HgButton } from '@smg-real-estate/domus';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { InformedContactModal } from '@/components/common';
import { VALUATION_FUNNEL_PATH } from '@/router/routes/constants';
import { trackingGenericClickEvent } from '@/tracking/tracking';
import {
  LOCAL_LANDING_PAGE_ACTION,
  LOCAL_LANDING_PAGE_CATEGORY,
  LOCAL_LANDING_PAGE_EVENT,
} from '@/tracking/types';
import { ContactModalFrom, ContactModalType } from '@/types';

const router = useRouter();
const { locale } = useI18n();

// Constants that were in data
const CallMeBackModalFrom = ContactModalFrom;

const contactModalRef = ref<InstanceType<typeof InformedContactModal> | null>(null);

const openContactModal = () => {
  trackingGenericClickEvent({
    event: LOCAL_LANDING_PAGE_EVENT.BUTTON_CLICK,
    category: LOCAL_LANDING_PAGE_CATEGORY.BUTTON_CLICK,
    action: LOCAL_LANDING_PAGE_ACTION.REQUEST_CALLBACK,
    label: 'recall_icon',
  });
  contactModalRef.value?.open();
};

const redirectToValuation = () => {
  trackingGenericClickEvent({
    event: LOCAL_LANDING_PAGE_EVENT.BUTTON_CLICK,
    category: LOCAL_LANDING_PAGE_CATEGORY.BUTTON_CLICK,
    action: LOCAL_LANDING_PAGE_ACTION.VALUATION_FUNNEL,
    label: 'evaluation_icon',
  });
  router.push(`${VALUATION_FUNNEL_PATH[locale.value]}`);
};
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

$viewport-xlarge: 1500px; // Edge case to handle the floating buttons position do not far from the content

.container {
  position: fixed;
  bottom: $space-200;
  z-index: 2;
  display: grid;
  width: calc(100% - 2 * $space-100);
  margin-left: $space-100;
  grid-template-columns: 1fr 1fr;

  @include grid-media-query(sm) {
    bottom: $space-200;
    left: 50%;
    gap: $space-200;
    width: auto;
    margin-left: 0;
    transform: translateX(-50%);
  }

  @include grid-media-query(md) {
    right: $space-100;
    left: unset;
    grid-template-columns: unset;
    gap: $space-100;
  }

  @media screen and (min-width: $viewport-xlarge) {
    right: calc((100% - $layout-inner-max-width) / 2);
  }
}

.button {
  flex: 1;

  &[class*='HgButton_button'][class*='HgButton_defaultSize'] {
    padding: $space-50;
  }

  &:first-child {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  &:last-child {
    margin-left: -1px;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  @include grid-media-query(sm) {
    flex: auto;
    gap: $space-50;
    width: auto;
    margin-left: 0;

    &[class*='HgButton_button'][class*='HgButton_defaultSize'] {
      padding: $space-75;
    }

    &:first-child,
    &:last-child {
      margin-left: 0;
      border: 1px solid $color-grey-100;
      border-radius: $border-radius-l;
    }
  }
}
</style>
